import * as React from 'react';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import clsx from 'clsx';
import { SwiperSlide, Swiper } from 'swiper/react';
import { Pagination } from 'swiper';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import { elementIsVisibleInViewport } from '../../lib/utils';
import FancyTitle from '../fancy-title';
import SplitPane from '../split-pane';
import StoryblokImage from './storyblok-image';
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/virtual';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
import useWindowSize from '../../hooks/useWindowSize';

export default function ScrollSlider({ blok }) {
  const items = blok.blocks;
  const {
    itemHeadline, subheadlineMobile, homePageSlider,
  } = blok;

  const ref = React.useRef();
  const windowSize = useWindowSize();

  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);

  const getHeight = (element) => {
    const subtitleMobileHeight = document.querySelectorAll(element);
    let maxHeight = 0;

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      subtitleMobileHeight[i].style = 'height: auto';
    }

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      if (subtitleMobileHeight[i].offsetHeight > maxHeight) {
        maxHeight = subtitleMobileHeight[i].offsetHeight;
      }
    }

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      subtitleMobileHeight[i].style = `height: ${maxHeight}px`;
    }
  };

  React.useEffect(() => {
    window.lastScrollY = 0;
    let animationFrameId = null;

    const handleImageVisibility = () => {
      if (window.innerWidth >= 1024) {
        if (ref.current) {
          const allImages = [...ref.current.querySelectorAll('[data-image]')];
          const refY = ref.current.getBoundingClientRect().y;

          if (refY < 0) {
            ref.current.querySelectorAll('[data-text]').forEach((item, index) => {
              if (index >= allImages.length - 1) return;
              const image = ref.current.querySelector(`[data-image="${item.dataset.text}"]`);
              if (elementIsVisibleInViewport(item, true)) {
                image?.classList.add('opacity-100');
                image?.classList.remove('opacity-0');
              } else {
                image?.classList.add('opacity-0');
                image?.classList.remove('opacity-100');
              }
            });
          }
        }
      }
      window.lastScrollY = window.scrollY;
    };

    const onScroll = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        handleImageVisibility();
      });
    };

    handleImageVisibility();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (!items.length) {
    return null;
  }

  return (
    <div
      {...storyblokEditable(blok)}
      className={clsx(
        homePageSlider && 'scroll-slider-homepage',
      )}
    >
      {/* {items && items.map((_blok) => (
        <StoryblokComponent blok={_blok} key={_blok._uid} />
      ))} */}
      <div className="flex flex-wrap lg:flex-nowrap h-full top-0 items-start lg:grid grid-cols-[1.5fr_1fr] auto-rows-fr lg:-ml-16 -ml-0 w-screen lg:w-[calc(100%+4rem)]" ref={ref}>
        {windowSize.width < 1024 ? (
          <>
            {itemHeadline && items && items[0].title && (
            <FancyTitle
              className="px-4"
              text={items[0].title}
              marginBottom="mb-10"
              tag="h2"
            />
            )}
            <Swiper
              modules={[Pagination]}
              direction="horizontal"
              spaceBetween={0}
              slidesPerView={1}
              className="h-full w-screen"
              pagination={{
                clickable: true,
                renderBullet: (index, className) => {
                  const label = currentLanguage === 'en'
                    ? `Go to slide ${index + 1}`
                    : `Zu Folie ${index + 1} gehen`;
                  return `<button class="${className}" aria-label="${label}"></button>`;                }
              }}
              a11y={{
                prevSlideMessage: currentLanguage === 'en' ? 'Previous slide' : 'Vorherige Folie',
                nextSlideMessage: currentLanguage === 'en' ? 'Next slide' : 'Nächste Folie',
                firstSlideMessage: currentLanguage === 'en' ? 'This is the first slide' : 'Dies ist die erste Folie',
                lastSlideMessage: currentLanguage === 'en' ? 'This is the last slide' : 'Dies ist die letzte Folie',
                paginationBulletMessage: currentLanguage === 'en' ? 'Go to slide {{index}}' : 'Zu Folie {{index}} gehen',
              }}
              onBeforeInit={() => getHeight('.subtitleMobile h3')}
              onResize={() => getHeight('.subtitleMobile h3')}
              autoHeight
            >
              {items && items.map((item, index) => (
                <SwiperSlide
                  className={clsx(
                    'w-full',
                    items.length > 1 ? 'cursor-grab' : '',
                  )}
                  key={item._uid || item.title}
                  id={item._uid || null}
                  role="group"
                  aria-label={`${index + 1} / ${items.length}${item.title ? `: ${item.title}` : ''}`}
                >
                  <SplitPane
                    className="h-full"
                    left={(
                      <div className="flex flex-col">
                        {(subheadlineMobile && !itemHeadline) && (
                        <div className="px-4 start subtitleMobile">
                          <FancyTitle
                            text={item.title || items[0].title}
                            subtitle={subheadlineMobile && item?.subtitle}
                            tag="h2"
                            marginBottom="mb-12 lg:mb-16 xl:mb-20"
                            textAlign="text-center lg:text-center"
                          />
                        </div>
                        )}
                        <StoryblokImage
                          data-swiper-parallax-y={400}
                          alt={getStoryblokAlt(item.image)}
                          src={item.image}
                          className="w-full h-[50vh] lg:max-h-[50vh] xl:max-h-[60vh] cover-image"
                          sizes="(max-width: 1400px) 100vw, 60vw"
                        />
                      </div>
                    )}
                    right={(
                      <div className="p-4 xll:p-24 pb-8 xll:pb-24 h-full flex flex-col justify-end">
                        <p
                          data-swiper-parallax-opacity={0}
                          data-swiper-parallax={-200}
                          dangerouslySetInnerHTML={{ __html: item.description }}
                          className={clsx((item.link_url && item.link_text) ? '' : 'mb-8')}
                        />
                        {(item.link_url && item.link_text) && (
                          <Link
                            to={item.link_url.url || item.link_url.cached_url}
                            title={item?.link_url?.link_title || undefined}
                            className="button-general !max-w-max !mx-auto !mb-8"
                          >
                            {item.link_text}
                            {item.title && (
                              <span className="sr-only">: {item.title}</span>
                            )}
                          </Link>
                        )}
                      </div>
                    )}
                    rightWidth="xll:w-2/5"
                    leftWidth="xll:w-3/5"
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        ) : (
          <>
            <div className="w-full grid place-content-center justify-left sticky top-0 h-screen col-start-1">
              {items && items.slice(0).reverse().map((item, index, array) => (
              // eslint-disable-next-line react/no-array-index-key
                <div key={`image-${index}`} className="transparent_background">
                  {item.image && (
                  <div
                    className={`${[array[index + 1]?.image.name, array[index - 1]?.image.name].includes(item.image.name) ? '' : 'transition-opacity duration-[300ms]'} absolute inset-0`}
                    data-image={array.length - index}
                    style={{ background: 'transparent' }}
                  >
                    <StoryblokImage
                      alt={getStoryblokAlt(item.image, item.title || (currentLanguage === 'en' ? 'Slider image' : 'Slider Bild'))}
                      src={item.image}
                      className="w-full h-full cover-image"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>
                  )}
                </div>
              ))}
            </div>

            {items && items.map((item, index) => (
              <div
                className="p-4 lg:p-8 xl:p-16 pb-8 lg:pb-24 h-full flex flex-col justify-center col-start-2 "
                key={item._uid || `${item.title}-${index}`}
                id={item._uid || null}
              >
                {item.title && (
                  <div data-text={index + 1}>
                    <FancyTitle
                      text={item.title}
                      subtitle={item.subtitle}
                      tag="h2"
                    />
                  </div>
                )}
                {!item.title && item.subtitle && (
                  <div data-text={index + 1}>
                    <p className="subtitle-class">{item.subtitle}</p>
                  </div>
                )}
                {!(item.title || item.subtitle) && (
                  <div data-text={index + 1} />
                )}

                <p dangerouslySetInnerHTML={{ __html: item.description }} />
                {(item.link_url && item.link_text) && (
                  <Link
                    to={item.link_url.url || item.link_url.cached_url}
                    title={item?.link_url?.link_title || undefined}
                    className="button-primary mt-4 max-w-max text-xl"
                  >
                    {item.link_text}
                    {item.title && (
                      <span className="sr-only">: {item.title}</span>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
